import queryString from 'query-string';
import _isEqual from 'lodash/isEqual';
import axios from 'axios';
import _get from 'lodash/get';
import * as assetsHelpers from '../../../helpers/assets';
import Logger from '../../../services/Logger';
import UiBlocker from '../../../services/UiBlocker';
import localization from '../../../shared/strings';
import * as utils from '../../../shared/utils';
import picsioConfig from '../../../../../../config';

import * as collectionHelpers from '../../helpers/collections';
import TYPES from '../../action-types';
import { changeBulkMediaMatchViewMode } from '../main';
import { navigateToRoot } from '../../../helpers/history';

import { showDialog } from '../../../components/dialog';
import getThumbnails from './getThumbnails';
import checkForCollectionsSortingsEquality from '../../../helpers/checkForCollectionsSortingsEquality';
import sdk from '../../../sdk';
import sendEventToIntercom from '../../../services/IntercomEventService';
import isVisualSearchEnabled from '../../../helpers/isVisualSearchEnabled';

/**
 * Get assets
 * @param {boolean} isNewRoute
 * @param {number?} delay - delay before request, e.g. updateByNew -> 2000
 */
const getAssets = (isNewRoute, delay, geoData, isMoreLoaded) => (
  dispatch,
  getState,
) => {
  function handleError(error, extraData) {
    UiBlocker.unblock();

    if (axios.isCancel(error)) return;

    let errorText = localization.CATALOG_VIEW.cantLoadData;
    const errorStatus = utils.getStatusFromResponceError(error);
    const errorSubcode = utils.getDataFromResponceError(error, 'subcode');
    const errorMessage = utils.getDataFromResponceError(error, 'msg');

    if (errorStatus === 404) {
      errorText = localization.CATALOG_VIEW.collectionNotFound;
    }
    if (errorStatus === 400 && errorSubcode === 'InvalidSearchQueryApiError') {
      errorText = localization.CATALOG_VIEW.errorCheckSearchQuery;
    }

    if (errorSubcode === 'MatchAssetsToProductsApiError') {
      Logger.log('User', 'BulkMediaMatchLoadFailed', {
        failureReason: errorMessage,
      });
    }

    const originalError = error && error.response
      ? {
        data: error.response.data,
        status: error.response.status,
        statusText: error.response.statusText,
      }
      : error;

    const errToJson = error && error.toJSON && typeof error.toJSON === 'function'
      ? error.toJSON()
      : JSON.stringify(error, null, 2);
    const errToString = error
      && error.toString
      && typeof error.toString === 'function'
      && error.toString();

    if (!error || errorStatus !== 400) {
      /** Don't send error if status === 400 -> incorrect search query */
      const connection = utils.getNavigatorConnectionInfo();
      Logger.error(
        new Error('Error fetching assets'),
        {
          error,
          showDialog: true,
          userDialogueMessage: errorText,
          connection,
          originalError,
          errToJson,
          errToString,
          extraData,
        },
        [
          'GetAssetsFailed',
          {
            errorMessage: errorText || 'NoMessage',
            userDialogueMessage: errorText,
            connection,
          },
        ],
      );
    }

    showDialog({
      title:
        errorStatus === 400
          ? localization.CATALOG_VIEW.errorCheckSearchQueryTitle
          : localization.CATALOG_VIEW.textError,
      text: errorText,
      textBtnCancel: null,
      onOk: navigateToRoot,
      onCancel: navigateToRoot,
    });

    if ((window.location.search || '').includes('isMediaMatch')) {
      dispatch(changeBulkMediaMatchViewMode(false));
    }

    dispatch({
      type: TYPES.ASSETS.FETCH.FAILED,
      error,
    });
  }

  function getIds() {
    /** Get current ids from search query */
    const {
      collectionIds,
      lightboardId,
      keywords,
      inboxId,
    } = queryString.parse(window.location.search);
    let collectionIdsArray = collectionIds;
    if (typeof collectionIds === 'string') {
      collectionIdsArray = [collectionIds];
    }
    return {
      collectionIds: collectionIdsArray,
      lightboardId,
      keywordId: (keywords && keywords[0]) || null,
      inboxId,
    };
  }

  async function doRequest(from) {
    try {
      const {
        collectionIds, lightboardId, keywordId, inboxId,
      } = getIds();
      const defaultSort = { type: 'uploadTime', order: 'desc' };
      const { isProofing } = picsioConfig;
      let sort = isProofing
        ? window.websiteConfig.sortType || defaultSort
        : defaultSort;
      if (collectionIds) {
        // we can't use getState().collections.activeCollection
        // because it updates after getAssets action
        const foundCollections = await collectionHelpers.forceFindTagWithTagId({
          collections: getState().collections.collections,
          search: getState().collections.search,
          collectionIds,
        });

        const collections = foundCollections.filter(
          (collection) => !!collection,
        );

        const [collection] = collections;

        if (collection) {
          let equalSortings = true;
          if (!isProofing) {
            equalSortings = checkForCollectionsSortingsEquality(
              collections.map((activeCollection) => {
                if (!activeCollection.sortType) return { ...activeCollection, sortType: defaultSort };
                return activeCollection;
              }),
            );
          }
          if (collectionIds.length > 1 && !equalSortings) {
            sort = isProofing
              ? window.websiteConfig.sortType || defaultSort
              : defaultSort;
          } else {
            sort = isProofing
              ? window.websiteConfig.sortType || collection.sortType
              : collection.sortType;
          }
        }
      }

      if (inboxId) {
        const { user, inboxes } = getState();
        const inbox = inboxes.inboxes.find(({ _id }) => _id === inboxId);
        if (inbox?.sortType) {
          const sortType = inbox.sortType.find(
            ({ userId }) => user._id === userId,
          );
          if (sortType) sort = sortType;
        }
      }

      if (lightboardId) {
        // we can't use getState().lightboards.activeLightboard
        // because it updates after getAssets action
        const { lightboards } = getState().lightboards;
        const activeLightboard = lightboards.find(
          (lb) => lb._id === lightboardId,
        );
        if (activeLightboard) {
          sort = activeLightboard.sortType;
        }
      }
      const {
        user,
        assets: { items: assets },
      } = getState();
      let response;
      if ((window.location.search || '').includes('isMediaMatch')) {
        const startTime = Date.now();
        response = (await sdk.assets.matchAssetsToProducts()).data;
        const loadTimeSeconds = (Date.now() - startTime) / 1000;
        const { productMatches, variantMatches } = response.images.reduce(
          (acc, { product }) => {
            const { type, _id } = product;
            const key = `${type}Matches`;
            if (!acc[key].includes(_id)) {
              acc[key].push(_id);
            }
            return acc;
          },
          { productMatches: [], variantMatches: [] },
        );

        Logger.log('User', 'BulkMediaMatchLoaded', {
          loadTimeSeconds,
          matchingStrategy: user?.team?.settings?.middlewares
            ?.matchAssetsToProducts?.strongMatch
            ? 'strongMatch'
            : 'partialMatch',
          suggestedAssetsCount: response.images.length,
          productMatchesCount: productMatches.length,
          variantMatchesCount: variantMatches.length,
        });
      } else {
        response = (await assetsHelpers.search(from, sort, geoData, assets))
          .data;
        if (!isMoreLoaded) {
          Logger.log('User', 'SearchResultsCount', {
            total: response.total,
          });
        }
        const { similarityThreshold, byImageId } = queryString.parse(window.location.search);
        if (byImageId) {
          Logger.log('User', 'FindSimilarImagesQueryPerformed', {
            results_count: response.images.length,
            similarity_score: Number(similarityThreshold || 0) * 100,
            tier_level: user?.addonsFeatures?.aiKit?.title || 'none',
          });
        }
        const enableVisualSearchSetting = isVisualSearchEnabled(user);

        if (
          (window.location.search || '').includes('text')
          && enableVisualSearchSetting
        ) {
          const { text } = queryString.parse(window.location.search);
          const isVisualSearchMatchFound = response.images.some(
            (image) => image.foundWithVisualSearch,
          );

          if (isVisualSearchMatchFound) {
            const timesCount = (utils.LocalStorage.get('VisualSearchQueryTimes') || 0) + 1;

            if (timesCount === 10) {
              sendEventToIntercom('VisualSearchQuery10Times');
            } else if (timesCount === 50) {
              sendEventToIntercom('VisualSearchQuery50Times');
            }
            utils.LocalStorage.set('VisualSearchQueryTimes', timesCount);
          }

          const {
            visualSearchResultsCount,
            broadSearchResultsCount,
          } = response.images.reduce(
            (acc, image) => {
              if (image.foundWithVisualSearch) {
                acc.visualSearchResultsCount += 1;
              } else {
                acc.broadSearchResultsCount += 1;
              }
              return acc;
            },
            { visualSearchResultsCount: 0, broadSearchResultsCount: 0 },
          );
          if (!isMoreLoaded) {
            Logger.log('User', 'VisualSearchQueryPerformed', {
              queryText: text,
              totalCount: response.total,
              'visible_results_count(50max)': response.images.length,
              visibleVisualSearchCount: visualSearchResultsCount,
              visibleBroadSearchCount: broadSearchResultsCount,
            });
          } else {
            Logger.log('User', 'SearchMoreResultLoaded', {
              queryText: text,
              totalCount: response.total,
              'visible_results_count(50max)': response.images.length,
              visibleVisualSearchCount: visualSearchResultsCount,
              visibleBroadSearchCount: broadSearchResultsCount,
            });
          }
        }
      }

      const {
        full,
        images,
        total,
        geo,
        suggestedSort,
        allGeoImages,
      } = response;

      // Check if user is searching something by text request and found nothing
      const { text } = getState().router.location.query;
      if (total === 0 && text) {
        Logger.log('UI', 'SearchNothingFound', { text });
      }

      const {
        collectionIds: currentCollectionIds,
        lightboardId: currentLightboardId,
        keywordId: currentKeywordId,
        inboxId: currentInboxId,
      } = getIds();
      /**
       * If params changed
       * (for example: fast switching betwen collections, slow internet connection)
       * do not dispatch event to store (other fetch already started)
       */
      if (
        (collectionIds?.length > 0
          && currentCollectionIds?.length > 0
          && !_isEqual(collectionIds, currentCollectionIds))
        || (lightboardId
          && currentLightboardId
          && lightboardId !== currentLightboardId)
        || (keywordId && currentKeywordId && keywordId !== currentKeywordId)
        || (inboxId && currentInboxId && inboxId !== currentInboxId)
      ) {
        return;
      }

      if (images || allGeoImages) {
        /** Get thumbnails for mages if no custom thumbnail */
        const ids = images.map((asset) => asset._id);
        if (ids.length) getThumbnails(ids)(dispatch, getState);

        dispatch({
          type: TYPES.ASSETS.FETCH.COMPLETE,
          payload: {
            isNewRoute,
            full,
            total,
            geoDataItems: allGeoImages || [],
            items: images,
            suggestedSort,
            geo,
          },
        });
        UiBlocker.unblock();
      } else {
        handleError(response, 'NO IMAGES IN RESPONSE');
      }
    } catch (error) {
      handleError(error);
    }
  }

  const from = isNewRoute || geoData ? 0 : getState().assets.items.length;
  dispatch({
    type: TYPES.ASSETS.FETCH.START,
    payload: { blockUI: isNewRoute },
  });

  if (delay) {
    UiBlocker.block('Processing files...');
    setTimeout(() => doRequest(from), delay);
  } else {
    doRequest(from);
  }
};

export default getAssets;
